import { useCallback, useContext, useEffect, useState } from "react";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";
import { ArchiveRestore, Button, NoticeBox } from "../../ui";
import { fetchMenuAPI, restoreItemAPI, restoreSectionAPI } from "../../../utils/api/menu";
import { localized, menuErrorMessage } from "../menuRole";
import { menuActionsText } from "../menuActionsText";

const when = (date, lang) => (date ? new Date(date).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB") : "");

// Archived sections/items of the selected menu, each restorable in place.
// Reads the menu with includeArchived; restores go through the audited API.
export default function ArchivedContentPanel({ menu, onMenuChange }) {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const t = menuActionsText(lang);
  const [full, setFull] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setFull(await fetchMenuAPI(menu._id, { includeArchived: true }));
      setError("");
    } catch (err) {
      setError(menuErrorMessage(err, lang));
    }
  }, [menu._id, lang]);

  // Reload whenever the menu changes (menu.version increments on every save).
  useEffect(() => {
    load();
  }, [load, menu.version]);

  const restore = async (kind, id) => {
    if (!can("menu.manage", { restaurantId: menu.restaurant?.id })) return;
    setBusyId(id);
    setError("");
    try {
      const res = kind === "section" ? await restoreSectionAPI(menu._id, id) : await restoreItemAPI(menu._id, id);
      onMenuChange(res.menu, t.restored);
    } catch (err) {
      setError(menuErrorMessage(err, lang));
    } finally {
      setBusyId(null);
    }
  };

  const sections = full?.sections || [];
  const archivedSections = sections.filter((s) => s.archivedAt);
  const archivedItems = sections.flatMap((s) => (s.items || []).filter((i) => i.archivedAt).map((i) => ({ item: i, section: s })));

  const row = (key, title, subtitle, action) => (
    <li key={key} className="menu-archived-row">
      <div>
        <strong>{title}</strong>
        <small className="menu-secondary">{subtitle}</small>
      </div>
      {action}
    </li>
  );

  return (
    <section className="menu-branches-panel menu-archived-panel" aria-labelledby="menu-archived-title">
      <header>
        <h4 id="menu-archived-title">{t.archivedContent}</h4>
      </header>
      {error && (
        <NoticeBox variant="danger" size="sm">
          {error}
        </NoticeBox>
      )}
      {!full && !error && <p className="menu-secondary">{t.loading}</p>}
      {full && !archivedSections.length && !archivedItems.length && <p className="menu-empty-state">{t.nothingArchived}</p>}
      {archivedSections.length > 0 && (
        <>
          <h5>{t.archivedSections}</h5>
          <ul className="menu-archived-list">
            {archivedSections.map((s) =>
              row(
                s._id,
                localized(s.name, lang),
                when(s.archivedAt, lang),
                <Button type="button" variant="primary" appearance="soft" size="xs" rounded icon={<ArchiveRestore size={14} />} loading={busyId === s._id} disabled={Boolean(busyId)} onClick={() => restore("section", s._id)}>
                  {t.restore}
                </Button>,
              ),
            )}
          </ul>
        </>
      )}
      {archivedItems.length > 0 && (
        <>
          <h5>{t.archivedItems}</h5>
          <ul className="menu-archived-list">
            {archivedItems.map(({ item, section }) =>
              row(
                item._id,
                localized(item.name, lang),
                `${localized(section.name, lang)} · ${when(item.archivedAt, lang)}`,
                section.archivedAt ? (
                  <small className="menu-secondary">{t.restoreSectionFirst}</small>
                ) : (
                  <Button type="button" variant="primary" appearance="soft" size="xs" rounded icon={<ArchiveRestore size={14} />} loading={busyId === item._id} disabled={Boolean(busyId)} onClick={() => restore("item", item._id)}>
                    {t.restore}
                  </Button>
                ),
              ),
            )}
          </ul>
        </>
      )}
    </section>
  );
}
